// Ported from Shoelace sl-format-bytes (MIT). See LICENSE-THIRD-PARTY.md.

/**
 * @component ry-format-bytes
 * @category  display
 * @summary   Formats a number as a human-readable bytes (or bits) value using `Intl.NumberFormat`. Pure display — no events, no methods, no slots. Set `value` (number of bytes/bits) and optionally `unit`, `display`, and `lang`.
 *
 * Ported from Shoelace's `<sl-format-bytes>`. Behavior is preserved verbatim:
 * picks the largest unit prefix (kilo, mega, giga, tera, peta) at which the
 * value is >= 1 of that unit, rounds to 3 significant digits, and delegates
 * the final string to `Intl.NumberFormat` with `style: 'unit'`. That means
 * the exact text (separator, abbreviation, pluralization) is whatever the
 * runtime locale renders.
 *
 * @attribute value    number?     [default=0]      The number to format, in bytes (or bits when `unit="bit"`). Non-numeric values render the empty string.
 * @attribute unit     'byte' | 'bit'?  [default='byte']  Whether the value represents bytes or bits. `bit` tops out at terabit (no petabit per Intl spec).
 * @attribute display  'long' | 'short' | 'narrow'?  [default='short']  Forwarded to `Intl.NumberFormat`'s `unitDisplay`. `short` → "1 kB", `long` → "1 kilobyte", `narrow` → "1kB".
 * @attribute lang     string?     [default=document.documentElement.lang]  BCP-47 language tag. Controls digit grouping, decimal separator, and unit pluralization.
 *
 * @content   None — the element's textContent is fully replaced on every render. Children are discarded.
 *
 * @dom
 *   ry-format-bytes[value][unit?][display?][lang?]
 *     > {formatted text — e.g. "1 kB", "1 kilobyte", "1kB"}
 *
 * @gotcha  Output is locale-dependent. `Intl.NumberFormat` in `en-US` renders "1 kB" for 1000 bytes; another locale may render "1 ko" or use a different separator. Don't assert exact strings in cross-locale tests — assert the numeric prefix and a substring.
 *
 * @gotcha  Uses SI (base-1000) prefixes, not binary (base-1024). 1024 bytes renders as "1.02 kB", not "1 KiB". This matches Shoelace upstream and the WHATWG/Intl unit registry.
 *
 * @gotcha  `unit="bit"` has no `petabit` entry (Intl doesn't ship one). Very large bit values clamp at terabit.
 *
 * @gotcha  Changing any of `value`, `unit`, `display`, or `lang` re-renders synchronously via `attributeChangedCallback`. There is no debouncing — set attributes once, not in a hot loop.
 *
 * @example basic
 * ```html
 * <ry-format-bytes value="1024"></ry-format-bytes>
 * ```
 *
 * @example short-default
 * ```html
 * <ry-format-bytes value="1500000" display="short"></ry-format-bytes>
 * ```
 *
 * @example long
 * ```html
 * <ry-format-bytes value="1000" display="long"></ry-format-bytes>
 * ```
 *
 * @example narrow
 * ```html
 * <ry-format-bytes value="1000" display="narrow"></ry-format-bytes>
 * ```
 *
 * @example bits
 * ```html
 * <ry-format-bytes value="1000000" unit="bit"></ry-format-bytes>
 * ```
 *
 * @tutorial default-renders-bytes
 * ```js
 * const el = mount('<ry-format-bytes value="1024"></ry-format-bytes>');
 * await expectEqual(el.textContent.includes('1.02'), true, 'value rounded to 3 sig figs');
 * await expectEqual(/k.?B/i.test(el.textContent), true, 'kB unit emitted');
 * ```
 *
 * @tutorial unit-bit-emits-bits
 * ```js
 * const el = mount('<ry-format-bytes value="1000000" unit="bit"></ry-format-bytes>');
 * // Intl renders the bit unit — exact abbreviation varies but the prefix should be "1" and contain "b" (not "B").
 * await expectEqual(el.textContent.startsWith('1'), true, 'starts with numeric 1');
 * await expectEqual(/M.?bit|Mbit|Mb/i.test(el.textContent), true, 'megabit-ish unit emitted');
 * ```
 *
 * @tutorial display-long-spells-out-unit
 * ```js
 * const el = mount('<ry-format-bytes value="1000" display="long"></ry-format-bytes>');
 * await expectEqual(/kilobyte/i.test(el.textContent), true, 'long display spells out kilobyte');
 * ```
 *
 * @tutorial attribute-change-rerenders
 * ```js
 * const el = mount('<ry-format-bytes value="1000"></ry-format-bytes>');
 * const before = el.textContent;
 * el.setAttribute('value', '2000');
 * const after = el.textContent;
 * await expectEqual(before !== after, true, 're-rendered after value change');
 * await expectEqual(after.startsWith('2'), true, 'new value reflected');
 * ```
 *
 * @see ry-stat
 */

import { RyElement } from '../core/ry-element.js';

type FormatBytesUnit = 'byte' | 'bit';
type FormatBytesDisplay = 'long' | 'short' | 'narrow';

export class RyFormatBytes extends RyElement {
  static observedAttributes = ['value', 'unit', 'display', 'lang'] as const;

  setup(): void {
    this.#render();
  }

  attributeChangedCallback(): void {
    // Only render if connected — connectedCallback → setup() handles the
    // initial render. attributeChangedCallback fires before connection for
    // parser-supplied attributes; rendering then would duplicate work.
    if (this.isConnected) this.#render();
  }

  get value(): number {
    const raw = this.getAttribute('value');
    if (raw === null) return 0;
    return Number(raw);
  }

  get unit(): FormatBytesUnit {
    const raw = this.getAttribute('unit');
    return raw === 'bit' ? 'bit' : 'byte';
  }

  get display(): FormatBytesDisplay {
    const raw = this.getAttribute('display');
    return raw === 'long' || raw === 'narrow' ? raw : 'short';
  }

  get lang(): string {
    return this.getAttribute('lang') || document.documentElement.lang || undefined as unknown as string;
  }

  #render(): void {
    const value = this.value;

    if (Number.isNaN(value)) {
      this.textContent = '';
      return;
    }

    // Mirror upstream Shoelace logic verbatim.
    const bitPrefixes = ['', 'kilo', 'mega', 'giga', 'tera']; // no petabit
    const bytePrefixes = ['', 'kilo', 'mega', 'giga', 'tera', 'peta'];
    const unit = this.unit;
    const prefix = unit === 'bit' ? bitPrefixes : bytePrefixes;
    const safeValue = value <= 0 ? 0 : value;
    const index = safeValue === 0
      ? 0
      : Math.max(0, Math.min(Math.floor(Math.log10(safeValue) / 3), prefix.length - 1));
    const composedUnit = (prefix[index] ?? '') + unit;
    const valueToFormat = parseFloat((safeValue / Math.pow(1000, index)).toPrecision(3));

    try {
      const formatter = new Intl.NumberFormat(this.lang || undefined, {
        style: 'unit',
        unit: composedUnit,
        unitDisplay: this.display,
      });
      this.textContent = formatter.format(valueToFormat);
    } catch {
      // Intl can throw on unsupported unit/locale combos. Fall back to a
      // plain numeric + composed unit string so output remains readable.
      this.textContent = `${valueToFormat} ${composedUnit}`;
    }
  }
}

customElements.define('ry-format-bytes', RyFormatBytes);
